/**
 * Page header used by every screen: title, optional description, primary actions on
 * the right, and the contextual "Help" link (FR-SEC-04) to this module's Help page.
 */
import { CircleHelp } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { pageTitle } from '../../config/brand';
import { cx } from './core';

export function PageHeader({ title, description, actions, help, breadcrumb, meta }: { title: string; description?: ReactNode; actions?: ReactNode; /** Help slug (src/app/help/content). */ help?: string; breadcrumb?: { label: string; to: string }[]; meta?: ReactNode }) {
  useEffect(() => {
    document.title = pageTitle(title);
  }, [title]);
  return (
    <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end">
      <div className="min-w-0 flex-1">
        {breadcrumb && breadcrumb.length > 0 && (
          <nav aria-label="Breadcrumb" className="mb-1 text-sm text-muted">
            {breadcrumb.map((b, i) => (
              <span key={b.to}>
                <Link to={b.to} className="hover:text-ink hover:underline">
                  {b.label}
                </Link>
                {i < breadcrumb.length - 1 && <span aria-hidden> / </span>}
              </span>
            ))}
          </nav>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="display text-[26px] leading-tight sm:text-[28px]">{title}</h1>
          {meta}
        </div>
        {description && <p className="mt-1 max-w-2xl text-sm text-muted">{description}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {help && (
          <Link to={`/help/${help}`} className="inline-flex min-h-touch items-center gap-1.5 rounded-tag px-3 text-sm font-semibold text-muted hover:bg-sunken hover:text-ink">
            <CircleHelp aria-hidden className="h-4 w-4" />
            Help
          </Link>
        )}
        {actions}
      </div>
    </header>
  );
}

/** Content width wrapper for pages. `narrow` for forms, `wide` for tables and dashboards. */
export function PageBody({ children, width = 'wide', className }: { children: ReactNode; width?: 'narrow' | 'wide'; className?: string }) {
  return <div className={cx('mx-auto w-full px-4 pb-16 pt-5 sm:px-6 lg:px-8', width === 'narrow' ? 'max-w-3xl' : 'max-w-[1400px]', className)}>{children}</div>;
}
